import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Select from 'react-select';
import {
    collection, query, onSnapshot, doc, updateDoc, addDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from '../config/firebase';
import { theme } from '../config/theme';
import { formOptions } from '../constants/formOptions';
import { ROLES } from '../constants/roles';
import {
    formatCurrency, formatNumberForDisplay, formatNumberForInput, parseAmountForState, formatComitenteForDisplay,
} from '../utils/formatters';
import { calcularFechaLiquidacionFci, getNextBusinessDay, toIsoDate } from '../utils/dateHelpers';
import { renderCommentCell } from '../utils/uiHelpers';
import { compareValues } from '../utils/sortHelpers';
import { comercialPorDefecto } from '../utils/rolesUsuario';
import { useHorizontalScrollSync } from '../hooks/useHorizontalScrollSync';
import { useStackedTable } from '../hooks/useStackedTable';
import { useMobileCollapse } from '../hooks/useMobileCollapse';
import TopScrollbar from './TopScrollbar';
import CollapsibleHeader from './common/CollapsibleHeader';
import {
    uploadInstruccionAttachment,
    deleteInstruccionAttachment,
    isAttachmentTypeAllowed,
    formatAttachmentSize,
    getClipboardAttachments,
    ALLOWED_ATTACHMENT_ACCEPT,
    MAX_ATTACHMENT_SIZE_BYTES,
} from '../utils/instruccionesAttachments';
import { useFechaHoy } from '../hooks/useFechaHoy';
import { exportRowsToXlsx } from '../utils/excelExport';
import {
    buildInstruccionesExportRows,
    ANCHOS_COLUMNAS_INSTRUCCIONES,
} from '../utils/instruccionesExport';
import useFondosFci from '../hooks/useFondosFci';
import { seedDefaultFondosFci, findFondoByNombre } from '../services/fondosFciService';
import { PencilIcon, TrashIcon } from './Icons';
import Modal from './common/Modal';
import DuplicateWarningModal from './DuplicateWarningModal';
import {
    findDuplicates, buildDuplicateGroups, getInstruccionDuplicateKeys,
} from '../utils/duplicateDetection';
import { subscribeByComitentes } from '../services/comitentesQueryService';
import { filterMisClientes } from '../utils/saldosScope';
import { useSaldosClientes } from '../hooks/useSaldosClientes';
import { buildSaldoPreview } from '../utils/instruccionesSaldoPreview';
import { useNombresComerciales } from '../hooks/useNombresComerciales';
import SaldoImpactoPanel from './instrucciones/SaldoImpactoPanel';
import TitulosEditor from './instrucciones/TitulosEditor';
import {
    TIPOS_TITULOS, createTituloRow, titulosToFormRows, getTitulosInstruccion,
    buildTitulosPayload, validateTitulosRows, resumirEspecies, sumarCantidades,
} from '../utils/instruccionesTitulos';

const styles = {
    container: { fontFamily: theme.typography.fontFamily },
    headerContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
    mainTitle: { fontFamily: theme.typography.titleFamily, fontSize: '2rem', color: theme.colors.secondary, margin: 0 },
    headerActions: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
    button: {
        fontFamily: theme.typography.fontFamily, backgroundColor: theme.colors.primary, color: theme.colors.textPrimary,
        border: 'none', borderRadius: theme.borderRadius, padding: '0.75rem 1.5rem', fontSize: '0.9rem',
        fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.2s', display: 'flex',
        alignItems: 'center', gap: '0.5rem',
    },
    secondaryButton: { backgroundColor: theme.colors.border, color: theme.colors.secondary },
    iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' },
    tableContainer: {
        overflowX: 'auto', backgroundColor: theme.colors.surface, borderRadius: theme.borderRadius,
        boxShadow: theme.shadows.soft,
    },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: '1700px' },
    th: {
        backgroundColor: '#F9FAFB', padding: '1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600',
        textTransform: 'uppercase', color: theme.colors.secondary, borderBottom: `1px solid ${theme.colors.border}`,
    },
    td: { padding: '1rem', borderBottom: `1px solid ${theme.colors.border}`, fontSize: '0.875rem', verticalAlign: 'middle' },
    enProcesoRow: { backgroundColor: theme.colors.warning },
    hechaRow: { backgroundColor: theme.colors.success },
    rechazadaRow: { backgroundColor: theme.colors.error },
    programadaRow: { backgroundColor: '#EEF1F5', color: theme.colors.textSecondary, fontStyle: 'italic' },
    ownRowMarker: {
        borderLeft: `4px solid ${theme.colors.primary}`,
        fontWeight: '600',
    },
    ownRowBadge: {
        display: 'inline-block', marginLeft: '0.4rem', padding: '0.1rem 0.45rem',
        borderRadius: '999px', backgroundColor: theme.colors.primary, color: theme.colors.textPrimary,
        fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    modalOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(17, 24, 39, 0.8)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000,
    },
    modalContent: {
        backgroundColor: theme.colors.background, padding: '2rem', borderRadius: '8px', width: '90%',
        maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', boxShadow: theme.shadows.soft,
    },
    modalTitle: { fontFamily: theme.typography.titleFamily, fontSize: '1.5rem', color: theme.colors.primary, margin: '0 0 1.5rem 0' },
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' },
    formGroup: { gridColumn: 'span 1' },
    formGroupFull: { gridColumn: 'span 3' },
    formGroupDouble: { gridColumn: 'span 2' },
    label: { display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: theme.colors.secondary },
    input: {
        width: '100%', padding: '0.75rem', border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius,
        fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: theme.typography.fontFamily,
    },
    select: {
        width: '100%', padding: '0.75rem', border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius,
        fontSize: '0.875rem', backgroundColor: theme.colors.surface, fontFamily: theme.typography.fontFamily, boxSizing: 'border-box',
    },
    textarea: {
        width: '100%', padding: '0.75rem', border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius,
        fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: theme.typography.fontFamily, minHeight: '80px', resize: 'vertical',
    },
    modalActions: { marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' },
    filterContainer: {
        marginBottom: '1rem', backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius, boxShadow: theme.shadows.soft,
        borderLeft: `3px solid ${theme.colors.primary}`,
    },
    filterHeader: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.75rem 1.25rem', borderBottom: `1px solid ${theme.colors.border}`,
    },
    filterHeaderLeft: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
    filterTitle: {
        fontFamily: theme.typography.titleFamily, fontSize: '0.85rem', fontWeight: '600',
        color: theme.colors.primary, margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    filterBadge: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: theme.colors.primary, color: theme.colors.textPrimary,
        fontSize: '0.65rem', fontWeight: '700', borderRadius: '10px',
        minWidth: '18px', height: '18px', padding: '0 5px',
    },
    filterGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: '0.75rem', padding: '1rem 1.25rem',
    },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
    filterLabel: {
        fontSize: '0.68rem', fontWeight: '600', textTransform: 'uppercase',
        color: theme.colors.textSecondary, letterSpacing: '0.05em',
    },
    filterInput: {
        padding: '0.5rem 0.65rem', border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius, fontSize: '0.8rem', fontFamily: theme.typography.fontFamily,
        boxSizing: 'border-box', width: '100%', transition: 'border-color 0.2s, box-shadow 0.2s',
        outline: 'none', color: theme.colors.secondary,
    },
    filterInputActive: { borderColor: theme.colors.primary, boxShadow: '0 0 0 2px rgba(26, 40, 62, 0.1)' },
    filterSelect: {
        padding: '0.5rem 0.65rem', border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius, fontSize: '0.8rem', fontFamily: theme.typography.fontFamily,
        backgroundColor: theme.colors.surface, boxSizing: 'border-box', width: '100%',
        transition: 'border-color 0.2s, box-shadow 0.2s', outline: 'none',
        color: theme.colors.secondary, cursor: 'pointer',
    },
    filterSelectActive: { borderColor: theme.colors.primary, boxShadow: '0 0 0 2px rgba(26, 40, 62, 0.1)' },
    clearFiltersButton: {
        padding: '0.4rem 0.85rem', border: 'none',
        borderRadius: theme.borderRadius, fontSize: '0.75rem', fontFamily: theme.typography.fontFamily,
        backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#DC2626', cursor: 'pointer',
        fontWeight: '500', transition: 'background-color 0.2s', whiteSpace: 'nowrap',
        display: 'flex', alignItems: 'center', gap: '0.3rem',
    },
    pillButton: {
        padding: '0.4rem 0.75rem', borderRadius: '999px', border: 'none', cursor: 'pointer',
        fontSize: '0.75rem', fontWeight: '600', fontFamily: theme.typography.fontFamily,
        backgroundColor: theme.colors.primary, color: theme.colors.textPrimary,
    },
    attachmentsSection: {
        gridColumn: 'span 3',
        border: `1px dashed ${theme.colors.border}`,
        borderRadius: theme.borderRadius,
        padding: '1rem',
        backgroundColor: '#FAFBFC',
    },
    attachmentsHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap',
    },
    attachmentsTitle: {
        fontSize: '0.875rem', fontWeight: '600', color: theme.colors.secondary, margin: 0,
        display: 'flex', alignItems: 'center', gap: '0.4rem',
    },
    requiredBadge: {
        display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '999px',
        backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#B45309',
        fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    attachmentHint: {
        fontSize: '0.75rem', color: theme.colors.textSecondary, margin: '0 0 0.75rem 0',
    },
    attachmentList: {
        display: 'flex', flexDirection: 'column', gap: '0.4rem',
    },
    attachmentItem: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius, padding: '0.5rem 0.75rem', gap: '0.75rem',
    },
    attachmentLink: {
        color: theme.colors.primary, fontWeight: '500', textDecoration: 'none',
        fontSize: '0.85rem', wordBreak: 'break-all', flex: 1,
    },
    attachmentMeta: {
        fontSize: '0.7rem', color: theme.colors.textSecondary, whiteSpace: 'nowrap',
    },
    attachmentCellLink: {
        color: theme.colors.primary, textDecoration: 'underline', fontSize: '0.8rem',
        cursor: 'pointer', background: 'none', border: 'none', padding: 0,
        fontFamily: 'inherit',
    },
    attachmentCellEmpty: {
        color: theme.colors.textSecondary, fontStyle: 'italic', fontSize: '0.8rem',
    },
    tituloLine: { whiteSpace: 'nowrap', lineHeight: 1.6 },
    viaEgresoBadge: {
        display: 'inline-block', marginLeft: '0.4rem', padding: '0.1rem 0.45rem',
        borderRadius: '999px', backgroundColor: theme.colors.primary, color: theme.colors.textPrimary,
        fontSize: '0.65rem', fontWeight: '700', whiteSpace: 'nowrap', letterSpacing: '0.04em',
    },
    duplicateBadge: {
        display: 'inline-block', marginLeft: '0.4rem', padding: '0.1rem 0.45rem',
        borderRadius: '999px', backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#B45309',
        fontSize: '0.65rem', fontWeight: '700', whiteSpace: 'nowrap', cursor: 'help',
    },
};

const safeFormatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleDateString('es-AR');
    }
    try {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) return date.toLocaleDateString('es-AR');
    } catch (e) {
        return 'Fecha inválida';
    }
    return 'Fecha inválida';
};

// Fecha en la que la instrucción se opera: la programada cuando es diferida y
// la de carga en el resto de los casos. Se precalcula por fila para que la
// columna se ordene con el mismo comparador que las demás.
const getFechaOperativa = (instr) => {
    const [y, m, d] = String(instr?.fechaProgramada || '').split('-').map(Number);
    if (y && m && d) return new Date(y, m - 1, d);
    return instr?.createdAt || null;
};

const TIPOS_DINERO = ['Ingreso', 'Egreso'];
const TIPOS_FCI = ['Suscripcion FCI', 'Rescate FCI'];

const getCategory = (tipo) => {
    if (TIPOS_DINERO.includes(tipo)) return 'dinero';
    if (TIPOS_FCI.includes(tipo)) return 'fci';
    if (TIPOS_TITULOS.includes(tipo)) return 'titulos';
    return null;
};

const getContraparteLabel = (tipo) => {
    const cat = getCategory(tipo);
    if (cat === 'dinero') return 'CBU / Banco destino';
    if (cat === 'titulos') return 'Comitente / Contraparte';
    return 'Contraparte';
};

// Cantidad y Especie de la tabla: con varias especies va una por renglón en las
// dos columnas, alineadas, para que cada cantidad quede junto a la suya.
const renderTitulosCells = (instr) => {
    const titulos = getTitulosInstruccion(instr);
    if (titulos.length <= 1) {
        return (
            <>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                    {instr.cantidad ? formatNumberForDisplay(instr.cantidad) : '-'}
                </td>
                <td style={styles.td}>{instr.especie || '-'}</td>
            </>
        );
    }
    return (
        <>
            <td style={{ ...styles.td, textAlign: 'right' }}>
                {titulos.map((titulo, idx) => (
                    <div key={idx} style={styles.tituloLine}>{formatNumberForDisplay(titulo.cantidad)}</div>
                ))}
            </td>
            <td style={styles.td}>
                {titulos.map((titulo, idx) => (
                    <div key={idx} style={styles.tituloLine}>{titulo.especie}</div>
                ))}
            </td>
        </>
    );
};

const VIA_EGRESO_DEFAULT = formOptions.viaEgreso[0];
const VIA_EGRESO_GC2 = 'MEP GC2';

const VIA_EGRESO_LABELS = {
    'Transferencia normal': 'Transferencia normal',
    [VIA_EGRESO_GC2]: 'MEP con código GC2',
};

const isUsdCableTransferencia = (tipo, moneda) => (
    TIPOS_DINERO.includes(tipo) && moneda === 'USD Cable'
);

// Los adjuntos van a Storage: un rechazo por permisos o cuota no se arregla
// reintentando, así que el mensaje tiene que distinguirlo de un corte de red.
const describeAttachmentUploadError = (error) => {
    switch (error?.code) {
        case 'storage/unauthorized':
            return 'Storage rechazó la subida por falta de permisos. Reintentar no lo resuelve: avisá a Sistemas.';
        case 'storage/unauthenticated':
            return 'Tu sesión expiró. Volvé a iniciar sesión y reintentá.';
        case 'storage/quota-exceeded':
            return 'No hay espacio disponible en Storage. Avisá a Sistemas.';
        case 'storage/retry-limit-exceeded':
        case 'storage/canceled':
            return 'La subida se interrumpió por la conexión. Intentá de nuevo.';
        default:
            return 'No se pudieron subir uno o más archivos. Intentá de nuevo.';
    }
};

const InstruccionForm = ({
    formState,
    saldoPreview,
    clientesDb,
    especies,
    handleInputChange,
    handleTitulosChange,
    isReadOnly,
    attachments = [],
    onAttachFiles,
    onRemoveAttachment,
    isUploading,
    fondosFci = [],
    onSeedFondosFci,
    isSeedingFondos = false,
    eligeComercial = false,
    nombresComerciales = [],
}) => {
    const fileInputRef = useRef(null);
    const requireAttachment = isUsdCableTransferencia(formState.tipo, formState.moneda);
    const minFechaProgramada = useMemo(() => toIsoDate(getNextBusinessDay()), []);

    const sortedEspecies = useMemo(() => {
        const seen = new Set();
        const unique = [];
        especies.forEach(esp => {
            const name = esp.nombre || '';
            if (!seen.has(name)) {
                seen.add(name);
                unique.push(esp);
            }
        });
        return unique.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' }));
    }, [especies]);

    const comitenteOptions = useMemo(() =>
        Object.keys(clientesDb).map(c => ({ value: c, label: formatComitenteForDisplay(c) })),
        [clientesDb]
    );

    const clienteOptions = useMemo(() =>
        Object.entries(clientesDb).map(([value, label]) => ({ value, label: `${label} - ${formatComitenteForDisplay(value)}` })),
        [clientesDb]
    );

    // Pegar capturas con Ctrl+V. El listener va en `document` porque el evento
    // `paste` solo lo recibe el elemento con foco, que puede ser el body: el
    // formulario vive dentro de un modal que se desmonta al cerrarse, así que
    // el listener solo está activo mientras el modal está abierto.
    useEffect(() => {
        if (isReadOnly || !onAttachFiles) return undefined;

        const handlePaste = (event) => {
            if (isUploading) return;
            const files = getClipboardAttachments(event.clipboardData);
            if (!files.length) return;
            // Si el foco está en un campo de texto y el portapapeles también trae
            // texto (copiar de Word o Excel trae texto + imagen), gana el pegado
            // normal del campo.
            const { tagName, isContentEditable } = event.target || {};
            const isTextField = tagName === 'INPUT' || tagName === 'TEXTAREA' || isContentEditable;
            if (isTextField && event.clipboardData?.getData('text')) return;
            event.preventDefault();
            onAttachFiles(files);
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [isReadOnly, isUploading, onAttachFiles]);

    const handleClientChange = useCallback((selectedOption) => {
        const nroComitente = selectedOption ? selectedOption.value : '';
        const cliente = nroComitente ? clientesDb[nroComitente] : '';
        handleInputChange({ target: { name: 'nroComitente', value: nroComitente } });
        handleInputChange({ target: { name: 'cliente', value: cliente } });
    }, [clientesDb, handleInputChange]);

    const category = getCategory(formState.tipo);
    const isRescateFci = formState.tipo === 'Rescate FCI';
    const isRescateTotal = isRescateFci && !!formState.esRescateTotal;
    const showMonto = (category === 'dinero' || category === 'fci') && !isRescateTotal;
    const showTitulos = category === 'titulos';
    const showFci = category === 'fci';
    const showViaEgreso = formState.tipo === 'Egreso';
    const showPlazoLiquidacion = isRescateFci;
    const showContraparte = category === 'dinero' || category === 'titulos';

    return (
        <div className="axon-form-grid" style={styles.formGrid}>
            <SaldoImpactoPanel preview={saldoPreview} />
            <div style={styles.formGroup}>
                <label style={styles.label}>Tipo de Instrucción</label>
                <select
                    name="tipo"
                    value={formState.tipo}
                    onChange={handleInputChange}
                    style={styles.select}
                    disabled={isReadOnly}
                >
                    {formOptions.tipoInstruccion.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            </div>
            <div style={styles.formGroup}>
                <label style={styles.label}>Nro Comitente</label>
                <Select
                    styles={{ control: (base) => ({ ...base, ...styles.input, padding: '0' }) }}
                    name="nroComitente"
                    options={comitenteOptions}
                    value={comitenteOptions.find(c => c.value === formState.nroComitente) || null}
                    onChange={handleClientChange}
                    placeholder="568 XXXXX"
                    isDisabled={isReadOnly}
                />
            </div>
            <div style={styles.formGroup}>
                <label style={styles.label}>Cliente</label>
                <Select
                    styles={{ control: (base) => ({ ...base, ...styles.input, padding: '0' }) }}
                    name="cliente"
                    options={clienteOptions}
                    value={clienteOptions.find(c => c.value === formState.nroComitente) || null}
                    onChange={handleClientChange}
                    placeholder="Buscar por nombre..."
                    isDisabled={isReadOnly}
                />
            </div>

            {showTitulos && (
                <TitulosEditor
                    rows={formState.titulos}
                    especies={sortedEspecies}
                    onChange={handleTitulosChange}
                    isReadOnly={isReadOnly}
                />
            )}

            {showMonto && (
                <div style={styles.formGroup}>
                    <label style={styles.label}>Monto</label>
                    <input
                        type="text"
                        inputMode="decimal"
                        name="montoInputValue"
                        value={formState.montoInputValue || ''}
                        onChange={handleInputChange}
                        onFocus={(e) => handleInputChange({ target: { name: 'montoInputValue', value: formatNumberForInput(e.target.value) } })}
                        onBlur={(e) => handleInputChange({ target: { name: 'montoInputValue', value: formatNumberForDisplay(parseAmountForState(e.target.value)) } })}
                        style={styles.input}
                        disabled={isReadOnly}
                    />
                </div>
            )}

            {(category === 'dinero' || category === 'fci') && (
                <div style={styles.formGroup}>
                    <label style={styles.label}>Moneda</label>
                    <select
                        name="moneda"
                        value={formState.moneda}
                        onChange={handleInputChange}
                        style={styles.select}
                        disabled={isReadOnly}
                    >
                        {formOptions.monedaInstruccion.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>
            )}

            {showViaEgreso && (
                <div style={styles.formGroup}>
                    <label style={styles.label}>Vía de transferencia</label>
                    <select
                        name="viaEgreso"
                        value={formState.viaEgreso || VIA_EGRESO_DEFAULT}
                        onChange={handleInputChange}
                        style={styles.select}
                        disabled={isReadOnly}
                    >
                        {formOptions.viaEgreso.map(opt => (
                            <option key={opt} value={opt}>{VIA_EGRESO_LABELS[opt] || opt}</option>
                        ))}
                    </select>
                    {formState.viaEgreso === VIA_EGRESO_GC2 && (
                        <p style={{ ...styles.attachmentHint, margin: '0.35rem 0 0' }}>
                            Código de rescate de FCI, exento de débitos y créditos.
                        </p>
                    )}
                </div>
            )}

            {isRescateFci && (
                <div style={{ ...styles.formGroupFull, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                        type="checkbox"
                        id="rescate-total-checkbox"
                        name="esRescateTotal"
                        checked={!!formState.esRescateTotal}
                        onChange={(e) => handleInputChange({ target: { name: 'esRescateTotal', value: e.target.checked } })}
                        style={{ width: 'auto' }}
                        disabled={isReadOnly}
                    />
                    <label htmlFor="rescate-total-checkbox" style={{ ...styles.label, marginBottom: 0 }}>
                        Rescate Total (no se indica monto)
                    </label>
                </div>
            )}

            {showFci && (
                <>
                    <div style={styles.formGroupDouble}>
                        <label style={styles.label}>Nombre del FCI</label>
                        <input
                            list="fondos-fci-list"
                            type="text"
                            name="fci"
                            value={formState.fci || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                handleInputChange({ target: { name: 'fci', value } });
                                const fondo = findFondoByNombre(fondosFci, value);
                                if (fondo && isRescateFci) {
                                    handleInputChange({
                                        target: { name: 'plazoLiquidacion', value: fondo.plazoLiquidacionDefault },
                                    });
                                }
                            }}
                            style={styles.input}
                            placeholder="Ej: IAM AHORRO PESOS CLASE A"
                            autoComplete="off"
                            disabled={isReadOnly}
                        />
                        <datalist id="fondos-fci-list">
                            {fondosFci.map(f => <option key={f.id || f.nombre} value={f.nombre} />)}
                        </datalist>
                        {fondosFci.length === 0 && !isReadOnly && onSeedFondosFci && (
                            <button
                                type="button"
                                onClick={onSeedFondosFci}
                                disabled={isSeedingFondos}
                                style={{
                                    ...styles.button,
                                    ...styles.secondaryButton,
                                    marginTop: '0.5rem',
                                    fontSize: '0.75rem',
                                    padding: '0.4rem 0.75rem',
                                }}
                            >
                                {isSeedingFondos ? 'Inicializando...' : '+ Inicializar catálogo de fondos'}
                            </button>
                        )}
                    </div>
                    {showPlazoLiquidacion && (
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Plazo Liquidación</label>
                            <select
                                name="plazoLiquidacion"
                                value={formState.plazoLiquidacion || 'T+0'}
                                onChange={handleInputChange}
                                style={styles.select}
                                disabled={isReadOnly}
                            >
                                {formOptions.plazoLiquidacionFci.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </>
            )}

            {showContraparte && (
                <div style={styles.formGroupFull}>
                    <label style={styles.label}>{getContraparteLabel(formState.tipo)}</label>
                    <input
                        type="text"
                        name="contraparte"
                        value={formState.contraparte || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        placeholder={
                            category === 'dinero' ? 'CBU, alias o banco destino' :
                            'Comitente origen / destino'
                        }
                        disabled={isReadOnly}
                    />
                </div>
            )}

            <div style={{ ...styles.formGroupFull, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                    type="checkbox"
                    id="programar-instruccion-checkbox"
                    name="esProgramada"
                    checked={!!formState.esProgramada}
                    onChange={(e) => handleInputChange({ target: { name: 'esProgramada', value: e.target.checked } })}
                    style={{ width: 'auto' }}
                    disabled={isReadOnly}
                />
                <label htmlFor="programar-instruccion-checkbox" style={{ ...styles.label, marginBottom: 0 }}>
                    Programar para una fecha futura (queda pendiente hasta ese día)
                </label>
            </div>

            {formState.esProgramada && (
                <div style={styles.formGroup}>
                    <label style={styles.label}>Fecha de desbloqueo</label>
                    <input
                        type="date"
                        name="fechaProgramada"
                        value={formState.fechaProgramada || ''}
                        min={minFechaProgramada}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={isReadOnly}
                    />
                    <p style={{ ...styles.attachmentHint, margin: '0.35rem 0 0' }}>
                        La instrucción se activará automáticamente al inicio de ese día hábil.
                    </p>
                </div>
            )}

            {eligeComercial && (
                <div style={styles.formGroupFull}>
                    <label style={styles.label}>Comercial</label>
                    <select
                        name="comercial"
                        value={formState.comercial || ''}
                        onChange={handleInputChange}
                        style={styles.select}
                        disabled={isReadOnly}
                        required
                    >
                        <option value="">Seleccioná por cuenta de qué comercial</option>
                        {[...new Set([...nombresComerciales, formState.comercial].filter(Boolean))].map(nombre => (
                            <option key={nombre} value={nombre}>{nombre}</option>
                        ))}
                    </select>
                </div>
            )}

            <div style={styles.formGroupFull}>
                <label style={styles.label}>Comentario Comercial</label>
                <textarea
                    name="comentarioComercial"
                    value={formState.comentarioComercial || ''}
                    onChange={handleInputChange}
                    style={styles.textarea}
                    placeholder="Detalle de la instrucción..."
                    disabled={isReadOnly}
                />
            </div>

            <div style={styles.attachmentsSection}>
                <div style={styles.attachmentsHeader}>
                    <h4 style={styles.attachmentsTitle}>
                        Comprobantes adjuntos
                        {requireAttachment && <span style={styles.requiredBadge}>Recomendado</span>}
                    </h4>
                    <div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept={ALLOWED_ATTACHMENT_ACCEPT}
                            onChange={(e) => {
                                if (onAttachFiles && e.target.files?.length) {
                                    onAttachFiles(Array.from(e.target.files));
                                }
                                e.target.value = '';
                            }}
                            style={{ display: 'none' }}
                            disabled={isReadOnly || isUploading}
                        />
                        <button
                            type="button"
                            style={{ ...styles.button, ...styles.secondaryButton }}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isReadOnly || isUploading}
                        >
                            {isUploading ? 'Subiendo...' : '+ Adjuntar archivos'}
                        </button>
                    </div>
                </div>
                <p style={styles.attachmentHint}>
                    {requireAttachment
                        ? 'Para transferencias en USD Cable se recomienda adjuntar el comprobante de titularidad de la cuenta bancaria del cliente.'
                        : 'Podés adjuntar imágenes, PDFs, Word o planillas. Máx. 10 MB por archivo.'}
                    {' '}También podés pegar una captura del portapapeles con Ctrl+V.
                </p>
                {attachments.length > 0 ? (
                    <div style={styles.attachmentList}>
                        {attachments.map((att, idx) => (
                            <div key={`${att.path}-${idx}`} style={styles.attachmentItem}>
                                <a
                                    href={att.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.attachmentLink}
                                >
                                    {att.name}
                                </a>
                                <span style={styles.attachmentMeta}>{formatAttachmentSize(att.size)}</span>
                                {!isReadOnly && (
                                    <button
                                        type="button"
                                        style={{ ...styles.iconButton, color: theme.colors.errorText }}
                                        onClick={() => onRemoveAttachment && onRemoveAttachment(idx)}
                                        title="Quitar adjunto"
                                    >
                                        <TrashIcon />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p style={{ ...styles.attachmentHint, fontStyle: 'italic', margin: 0 }}>
                        No hay archivos adjuntos.
                    </p>
                )}
            </div>
        </div>
    );
};

/*
 * Props de modo productor (opcionales):
 * - comitentesFilter: lista de nroComitente. Si se pasa, la suscripción se acota a
 *   esos comitentes (única lectura que las reglas le permiten a un productor).
 * - attachmentFolder: carpeta de Storage donde sube adjuntos (la propia del
 *   productor; las reglas solo le permiten tocar sus archivos).
 *
 * - filtroMisClientes (rol Comercial): filtro de vista "Mis clientes", aplicado
 *   después de numerar (ver saldosScope.filterMisClientes).
 */
const InstruccionesTab = ({ userRole, userName, userId, clientesDb, especies = [], onViewComment, comitentesFilter = null, attachmentFolder = null, filtroMisClientes = null }) => {
    const esModoProductor = comitentesFilter !== null;
    // Nombre del archivo exportado; se mantiene correcto aunque la planilla
    // quede abierta de un día para el otro.
    const fechaHoy = useFechaHoy();
    const comitentesKey = esModoProductor ? [...comitentesFilter].sort().join('|') : null;
    const [instrucciones, setInstrucciones] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formError, setFormError] = useState('');
    const [editingInstruccion, setEditingInstruccion] = useState(null);
    const [rejectionModal, setRejectionModal] = useState({ isOpen: false, instr: null });
    const [rejectionComment, setRejectionComment] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
    const [filters, setFilters] = useState({});
    const filtersPanel = useMobileCollapse();
    const stackedTable = useStackedTable({
        title: 'Cliente',
        primary: ['ID', 'Tipo', 'Monto', 'Especie', 'Cantidad', 'Moneda', 'Fecha', 'Estado'],
        actions: 'Acciones',
    });
    const [attachments, setAttachments] = useState([]);
    const [pendingDeletePaths, setPendingDeletePaths] = useState([]);
    const [newUploadPaths, setNewUploadPaths] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [attachmentsModal, setAttachmentsModal] = useState({ isOpen: false, instr: null });
    const [duplicateModal, setDuplicateModal] = useState({
        isOpen: false, mode: 'alta', matches: [], candidate: null, pendingAction: null,
    });
    const { fondos: fondosFci } = useFondosFci();
    const { getSaldoComitente } = useSaldosClientes();
    const [isSeedingFondos, setIsSeedingFondos] = useState(false);

    const handleSeedFondos = useCallback(async () => {
        if (isSeedingFondos) return;
        setIsSeedingFondos(true);
        try {
            const created = await seedDefaultFondosFci();
            alert(created > 0
                ? `Catálogo de fondos inicializado: ${created} fondos creados.`
                : 'El catálogo de fondos ya estaba inicializado.');
        } catch (error) {
            console.error('Error inicializando catálogo de fondos:', error);
            alert('No se pudo inicializar el catálogo de fondos. Revisá la consola.');
        } finally {
            setIsSeedingFondos(false);
        }
    }, [isSeedingFondos]);

    const isComercial = userRole === ROLES.COMERCIAL;
    const isMiddleOffice = userRole === ROLES.MIDDLE_OFFICE;
    const isAdmin = userRole === ROLES.ADMIN || userRole === ROLES.SUPER_ADMIN;
    // Middle office también carga, en apoyo del área comercial: elige el comercial.
    // El admin también elige; arranca con su propio nombre.
    const canCreate = isComercial || isMiddleOffice || isAdmin;
    const eligeComercial = isMiddleOffice || isAdmin;
    const nombresComerciales = useNombresComerciales(eligeComercial);
    const canManage = isMiddleOffice || isAdmin;

    // Mismas condiciones que habilitan el lapiz en la fila. Se reutiliza para
    // decidir si el aviso de duplicado puede ofrecer "Editar la existente".
    const canEditInstruccion = useCallback((instr) => (
        canCreate && !!instr && instr.creadoPorId === userId
        && (instr.estado === 'Pendiente' || instr.estado === 'Rechazada' || instr.estado === 'Programada')
    ), [canCreate, userId]);

    const createInitialFormState = useCallback(() => ({
        tipo: 'Ingreso',
        nroComitente: '',
        cliente: '',
        moneda: 'Pesos',
        montoInputValue: '',
        titulos: [createTituloRow()],
        fci: '',
        contraparte: '',
        viaEgreso: VIA_EGRESO_DEFAULT,
        comentarioComercial: '',
        comercial: comercialPorDefecto({ userRole, userName }),
        esRescateTotal: false,
        plazoLiquidacion: 'T+0',
        esProgramada: false,
        fechaProgramada: '',
    }), [userRole, userName]);

    const [formState, setFormState] = useState(createInitialFormState());

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormState(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleTitulosChange = useCallback((updater) => {
        setFormState(prev => ({ ...prev, titulos: updater(prev.titulos || []) }));
    }, []);

    const handleAttachFiles = useCallback(async (files) => {
        if (!files?.length) return;
        setFormError('');
        const invalid = files.find(f => !isAttachmentTypeAllowed(f));
        if (invalid) {
            setFormError(
                invalid.size > MAX_ATTACHMENT_SIZE_BYTES
                    ? `El archivo "${invalid.name}" supera el máximo de 10 MB.`
                    : `El tipo de archivo "${invalid.name}" no está permitido.`
            );
            return;
        }
        setIsUploading(true);
        try {
            const uploaded = [];
            for (const file of files) {
                const meta = await uploadInstruccionAttachment(file, {
                    uploadedById: userId,
                    uploadedByName: userName,
                    folder: attachmentFolder || undefined,
                });
                uploaded.push(meta);
            }
            setAttachments(prev => [...prev, ...uploaded]);
            setNewUploadPaths(prev => [...prev, ...uploaded.map(u => u.path)]);
        } catch (error) {
            console.error('Error subiendo adjuntos:', { code: error?.code, error });
            setFormError(describeAttachmentUploadError(error));
        } finally {
            setIsUploading(false);
        }
    }, [userId, userName, attachmentFolder]);

    const handleRemoveAttachment = useCallback((index) => {
        const removed = attachments[index];
        if (!removed) return;
        const wasNew = newUploadPaths.includes(removed.path);
        if (wasNew) {
            deleteInstruccionAttachment(removed.path);
            setNewUploadPaths(p => p.filter(path => path !== removed.path));
        } else {
            setPendingDeletePaths(p => [...p, removed.path]);
        }
        setAttachments(prev => prev.filter((_, i) => i !== index));
    }, [attachments, newUploadPaths]);

    const handleFilterChange = useCallback((field, value) => {
        setFilters(prev => {
            if (!value) {
                const { [field]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [field]: value };
        });
    }, []);

    const clearFilters = useCallback(() => setFilters({}), []);
    const hasActiveFilters = Object.keys(filters).length > 0;

    useEffect(() => {
        setIsLoading(true);
        if (comitentesKey !== null) {
            const comitentes = comitentesKey ? comitentesKey.split('|') : [];
            return subscribeByComitentes('instrucciones', comitentes, (docs) => {
                setInstrucciones(docs.filter(i => i.estado !== 'Eliminada'));
                setIsLoading(false);
            }, () => setIsLoading(false));
        }
        const unsubscribe = onSnapshot(query(collection(db, "instrucciones")), (snapshot) => {
            const data = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(i => i.estado !== 'Eliminada');
            setInstrucciones(data);
            setIsLoading(false);
        }, (error) => {
            console.error("Error al obtener las instrucciones:", error);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [comitentesKey]);

    const instruccionesConId = useMemo(() => {
        const sorted = [...instrucciones].sort((a, b) => {
            const getPriority = (estado) => (
                estado === 'En Proceso' || estado === 'Pendiente' || estado === 'Rechazada' || estado === 'Programada' ? 0 : 1
            );
            const pA = getPriority(a.estado);
            const pB = getPriority(b.estado);
            if (pA !== pB) return pA - pB;
            const dA = a.createdAt?.toDate?.() || 0;
            const dB = b.createdAt?.toDate?.() || 0;
            return dA - dB;
        });
        return sorted.map((i, idx) => ({
            ...i,
            persistentId: idx + 1,
            fechaOperativa: getFechaOperativa(i),
        }));
    }, [instrucciones]);

    // docId -> otras instrucciones que comparten cliente, tipo, instrumento y dia.
    const duplicateGroups = useMemo(
        () => buildDuplicateGroups(instruccionesConId, getInstruccionDuplicateKeys),
        [instruccionesConId],
    );

    const uniqueComerciales = useMemo(() => {
        const values = new Set(instrucciones.map(i => i.comercial).filter(Boolean));
        return [...values].sort();
    }, [instrucciones]);

    const uniqueMiddleOffice = useMemo(() => {
        const values = new Set(instrucciones.map(i => i.middleOffice).filter(Boolean));
        return [...values].sort();
    }, [instrucciones]);

    const filteredInstrucciones = useMemo(() => {
        const visibles = filterMisClientes(instruccionesConId, filtroMisClientes);
        if (!hasActiveFilters) return visibles;
        return visibles.filter(i => {
            if (filters.tipo && i.tipo !== filters.tipo) return false;
            if (filters.nroComitente && !i.nroComitente?.toLowerCase().includes(filters.nroComitente.toLowerCase())) return false;
            if (filters.cliente && !i.cliente?.toLowerCase().includes(filters.cliente.toLowerCase())) return false;
            if (filters.moneda && i.moneda !== filters.moneda) return false;
            if (filters.comercial && i.comercial !== filters.comercial) return false;
            if (filters.middleOffice && i.middleOffice !== filters.middleOffice) return false;
            if (filters.estado && i.estado !== filters.estado) return false;
            return true;
        });
    }, [instruccionesConId, filtroMisClientes, filters, hasActiveFilters]);

    const sortedInstrucciones = useMemo(() => {
        const items = [...filteredInstrucciones];
        const getStatusPriority = (estado) => (
            estado === 'En Proceso' || estado === 'Pendiente' || estado === 'Rechazada' || estado === 'Programada' ? 0 : 1
        );
        items.sort((a, b) => {
            const pA = getStatusPriority(a.estado);
            const pB = getStatusPriority(b.estado);
            if (pA !== pB) return pA - pB;
            if (sortConfig !== null) {
                return compareValues(a[sortConfig.key], b[sortConfig.key], sortConfig.direction);
            }
            return 0;
        });
        return items;
    }, [filteredInstrucciones, sortConfig]);

    // Descarga lo que está viendo el usuario: filtros y orden ya aplicados.
    const handleExportExcel = useCallback(() => {
        const exportado = exportRowsToXlsx(buildInstruccionesExportRows(sortedInstrucciones), {
            fileName: `Instrucciones-${fechaHoy}`,
            sheetName: 'Instrucciones',
            columnWidths: ANCHOS_COLUMNAS_INSTRUCCIONES,
        });
        if (!exportado) window.alert('No hay instrucciones para exportar.');
    }, [sortedInstrucciones, fechaHoy]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
        setSortConfig({ key, direction });
    };

    const { topRef, contentRef, contentWidth, onTopScroll, onContentScroll } =
        useHorizontalScrollSync([sortedInstrucciones, isLoading]);

    const openNewModal = () => {
        setEditingInstruccion(null);
        setFormState(createInitialFormState());
        setAttachments([]);
        setPendingDeletePaths([]);
        setNewUploadPaths([]);
        setFormError('');
        setIsModalOpen(true);
    };

    const handleEditClick = (instr) => {
        setEditingInstruccion(instr);
        setFormState({
            tipo: instr.tipo,
            nroComitente: instr.nroComitente || '',
            cliente: instr.cliente || '',
            moneda: instr.moneda || 'Pesos',
            montoInputValue: instr.esRescateTotal
                ? ''
                : (instr.monto ? formatNumberForDisplay(instr.monto) : ''),
            titulos: titulosToFormRows(instr),
            fci: instr.fci || '',
            contraparte: instr.contraparte || '',
            viaEgreso: instr.viaEgreso || VIA_EGRESO_DEFAULT,
            comentarioComercial: instr.comentarioComercial || '',
            comercial: instr.comercial || '',
            esRescateTotal: !!instr.esRescateTotal,
            plazoLiquidacion: instr.plazoLiquidacion || 'T+0',
            esProgramada: instr.estado === 'Programada' || !!instr.esProgramada,
            fechaProgramada: instr.fechaProgramada || '',
        });
        setAttachments(Array.isArray(instr.adjuntos) ? [...instr.adjuntos] : []);
        setPendingDeletePaths([]);
        setNewUploadPaths([]);
        setFormError('');
        setIsModalOpen(true);
    };

    const handleCancelModal = useCallback(() => {
        newUploadPaths.forEach(path => deleteInstruccionAttachment(path));
        setAttachments([]);
        setPendingDeletePaths([]);
        setNewUploadPaths([]);
        setFormError('');
        setIsModalOpen(false);
        setEditingInstruccion(null);
    }, [newUploadPaths]);

    // El panel de saldo solo se arma con el modal abierto: fuera de eso no hay
    // nada que proyectar y el memo no tiene que reaccionar a cada tecla del form.
    const saldoPreview = useMemo(() => (
        isModalOpen
            ? buildSaldoPreview({ formState, editingInstruccion, getSaldoComitente, fechaHoy })
            : null
    ), [isModalOpen, formState, editingInstruccion, getSaldoComitente, fechaHoy]);

    const buildPayloadFromForm = () => {
        const category = getCategory(formState.tipo);
        const isFci = category === 'fci';
        const isRescateTotal = formState.tipo === 'Rescate FCI' && !!formState.esRescateTotal;
        const monto = (category === 'dinero' || category === 'fci') && !isRescateTotal
            ? (parseFloat(parseAmountForState(formState.montoInputValue)) || 0)
            : 0;
        // `especie` y `cantidad` resumen la lista para quien no la conoce (backup
        // del cierre, orden de la tabla): especies separadas por coma y VN total.
        const titulos = category === 'titulos' ? buildTitulosPayload(formState.titulos) : [];
        const isSuscripcionFci = formState.tipo === 'Suscripcion FCI';
        const plazoLiquidacion = isFci
            ? (isSuscripcionFci ? 'T+0' : (formState.plazoLiquidacion || 'T+0'))
            : '';

        const esProgramada = !!formState.esProgramada && !!formState.fechaProgramada;
        const fechaProgramada = esProgramada ? formState.fechaProgramada : '';
        // La fechaLiquidacion FCI se calcula desde el día de ejecución real: la fecha
        // programada (si aplica) o la de creación. Parseamos YYYY-MM-DD como fecha LOCAL;
        // new Date('YYYY-MM-DD') se interpretaría como UTC y en ART adelantaría un día.
        let baseDate;
        if (esProgramada) {
            const [y, m, d] = fechaProgramada.split('-').map(Number);
            baseDate = new Date(y, m - 1, d);
        } else {
            baseDate = editingInstruccion?.createdAt?.toDate?.() || new Date();
        }
        const fechaLiquidacion = isFci ? calcularFechaLiquidacionFci(plazoLiquidacion, baseDate) : '';

        return {
            tipo: formState.tipo,
            nroComitente: formState.nroComitente || '',
            cliente: formState.cliente || '',
            moneda: (category === 'dinero' || category === 'fci') ? (formState.moneda || 'Pesos') : '',
            monto,
            cantidad: sumarCantidades(titulos),
            especie: resumirEspecies(titulos),
            titulos,
            fci: isFci ? (formState.fci || '') : '',
            contraparte: isFci ? '' : (formState.contraparte || ''),
            viaEgreso: formState.tipo === 'Egreso' ? (formState.viaEgreso || VIA_EGRESO_DEFAULT) : '',
            comentarioComercial: formState.comentarioComercial || '',
            esRescateTotal: isRescateTotal,
            plazoLiquidacion,
            fechaLiquidacion,
            esProgramada,
            fechaProgramada,
        };
    };

    const validateForm = () => {
        if (!formState.tipo) return 'Debe seleccionar un tipo de instrucción.';
        if (eligeComercial && !formState.comercial) return 'Seleccioná por cuenta de qué comercial cargás la instrucción.';
        if (!formState.nroComitente || !clientesDb[formState.nroComitente]) {
            return 'Debe seleccionar un cliente válido.';
        }
        const category = getCategory(formState.tipo);
        const isRescateTotal = formState.tipo === 'Rescate FCI' && !!formState.esRescateTotal;
        if ((category === 'dinero' || category === 'fci') && !isRescateTotal) {
            const monto = parseFloat(parseAmountForState(formState.montoInputValue)) || 0;
            if (monto <= 0) return 'El monto debe ser mayor a cero.';
        }
        if (category === 'fci' && !formState.fci?.trim()) {
            return 'Debe indicar el nombre del FCI.';
        }
        if (category === 'titulos') {
            const titulosError = validateTitulosRows(formState.titulos);
            if (titulosError) return titulosError;
        }
        if (formState.esProgramada) {
            if (!formState.fechaProgramada) return 'Debe indicar la fecha de desbloqueo.';
            if (formState.fechaProgramada < toIsoDate(getNextBusinessDay())) {
                return 'La fecha de desbloqueo debe ser un día hábil futuro.';
            }
            const [y, m, d] = formState.fechaProgramada.split('-').map(Number);
            const dow = new Date(y, m - 1, d).getDay();
            if (dow === 0 || dow === 6) return 'La fecha de desbloqueo no puede caer en fin de semana.';
        }
        return null;
    };

    const executeSubmit = async (formPayload) => {
        try {
            const nuevoEstado = formPayload.esProgramada ? 'Programada' : 'Pendiente';
            const adjuntosPayload = attachments.map(a => ({
                name: a.name || '',
                path: a.path || '',
                url: a.url || '',
                type: a.type || '',
                size: a.size || 0,
                uploadedAt: a.uploadedAt || new Date().toISOString(),
                uploadedById: a.uploadedById || '',
                uploadedByName: a.uploadedByName || '',
            }));
            if (editingInstruccion) {
                const docRef = doc(db, "instrucciones", editingInstruccion.id);
                await updateDoc(docRef, {
                    ...formPayload,
                    adjuntos: adjuntosPayload,
                    estado: nuevoEstado,
                    comentarioMiddleOffice: '',
                    middleOffice: '',
                    tomadaPorId: '',
                    tomadaPorNombre: '',
                    updatedAt: serverTimestamp(),
                    ...(eligeComercial ? { comercial: formState.comercial } : {}),
                });
            } else {
                await addDoc(collection(db, "instrucciones"), {
                    ...formPayload,
                    adjuntos: adjuntosPayload,
                    estado: nuevoEstado,
                    comercial: eligeComercial ? formState.comercial : userName,
                    middleOffice: '',
                    comentarioMiddleOffice: '',
                    creadoPorId: userId,
                    creadoPorNombre: userName,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    tomadaPorId: '',
                    tomadaPorNombre: '',
                });
            }
            pendingDeletePaths.forEach(path => deleteInstruccionAttachment(path));
            setAttachments([]);
            setPendingDeletePaths([]);
            setNewUploadPaths([]);
            setFormError('');
            setIsModalOpen(false);
            setEditingInstruccion(null);
        } catch (error) {
            console.error("Error guardando la instrucción:", error);
            setFormError("No se pudo guardar la instrucción.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isUploading) {
            setFormError('Esperá a que terminen de subirse los adjuntos.');
            return;
        }
        const validationError = validateForm();
        if (validationError) {
            setFormError(validationError);
            return;
        }

        const formPayload = buildPayloadFromForm();
        // Avisa (sin bloquear) si ya hay una instruccion equivalente del dia: el
        // caso tipico es recargar la misma operacion con el monto corregido.
        const matches = findDuplicates(formPayload, instruccionesConId, getInstruccionDuplicateKeys, {
            excludeId: editingInstruccion?.id,
        });
        if (matches.length > 0) {
            setDuplicateModal({
                isOpen: true,
                mode: 'alta',
                matches,
                candidate: formPayload,
                pendingAction: () => executeSubmit(formPayload),
            });
            return;
        }
        await executeSubmit(formPayload);
    };

    // Descarta la carga en curso (incluidos los adjuntos ya subidos) y abre la
    // instruccion existente para corregirla, que es lo que corresponde cuando el
    // aviso de duplicado acierta.
    const handleEditFromDuplicate = (match) => {
        newUploadPaths.forEach(path => deleteInstruccionAttachment(path));
        setNewUploadPaths([]);
        setAttachments([]);
        setPendingDeletePaths([]);
        setDuplicateModal({ isOpen: false, mode: 'alta', matches: [], candidate: null, pendingAction: null });
        handleEditClick(match);
    };

    const handleDelete = async (instruccionId) => {
        if (!window.confirm("¿Está seguro de que desea eliminar esta instrucción?")) return;
        const docRef = doc(db, "instrucciones", instruccionId);
        await updateDoc(docRef, {
            estado: 'Eliminada',
            eliminadoPorId: userId,
            eliminadoPorNombre: userName,
            eliminadoAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
    };

    const handleTomar = async (instr) => {
        if (!canManage) return;
        const docRef = doc(db, "instrucciones", instr.id);
        await updateDoc(docRef, {
            estado: 'En Proceso',
            middleOffice: userName,
            tomadaPorId: userId,
            tomadaPorNombre: userName,
            tomadaAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
    };

    const applyStatusChange = async (instr, newStatus) => {
        const payload = {
            estado: newStatus,
            updatedAt: serverTimestamp(),
        };
        if (newStatus === 'En Proceso' && !instr.middleOffice) {
            payload.middleOffice = userName;
            payload.tomadaPorId = userId;
            payload.tomadaPorNombre = userName;
            payload.tomadaAt = serverTimestamp();
        }
        if (newStatus === 'Hecha') {
            payload.finalizadaAt = serverTimestamp();
            if (!instr.middleOffice) payload.middleOffice = userName;
        }
        const docRef = doc(db, "instrucciones", instr.id);
        await updateDoc(docRef, payload);
    };

    const handleStatusChange = async (instr, newStatus) => {
        if (!canManage) return;
        if (newStatus === 'Rechazada') {
            setRejectionComment('');
            setRejectionModal({ isOpen: true, instr });
            return;
        }
        // Segunda linea de defensa: dejar en "Hecha" las dos instrucciones de un
        // par duplicado es lo que termina dejando al cliente deudor.
        const duplicados = newStatus === 'Hecha' ? (duplicateGroups.get(instr.id) || []) : [];
        if (duplicados.length > 0) {
            setDuplicateModal({
                isOpen: true,
                mode: 'procesamiento',
                matches: duplicados,
                candidate: instr,
                pendingAction: () => applyStatusChange(instr, newStatus),
            });
            return;
        }
        await applyStatusChange(instr, newStatus);
    };

    const handleRejectionSubmit = async () => {
        if (!rejectionComment.trim()) {
            alert("El comentario es obligatorio para rechazar una instrucción.");
            return;
        }
        const docRef = doc(db, "instrucciones", rejectionModal.instr.id);
        await updateDoc(docRef, {
            estado: 'Rechazada',
            middleOffice: userName,
            comentarioMiddleOffice: rejectionComment,
            updatedAt: serverTimestamp(),
        });
        setRejectionModal({ isOpen: false, instr: null });
        setRejectionComment('');
    };

    const getRowStyle = (estado) => {
        switch (estado) {
            case 'En Proceso': return styles.enProcesoRow;
            case 'Hecha': return styles.hechaRow;
            case 'Rechazada': return styles.rechazadaRow;
            case 'Programada': return styles.programadaRow;
            default: return {};
        }
    };

    const tableHeaders = [
        { key: 'persistentId', label: 'ID' },
        { key: 'actions', label: 'Acciones' },
        { key: 'fechaOperativa', label: 'Fecha' },
        { key: 'createdAt', label: 'Fecha de Carga' },
        { key: 'tipo', label: 'Tipo' },
        { key: 'nroComitente', label: 'Nro Comitente' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'monto', label: 'Monto' },
        { key: 'cantidad', label: 'Cantidad' },
        { key: 'especie', label: 'Especie' },
        { key: 'fci', label: 'FCI' },
        { key: 'plazoLiquidacion', label: 'Plazo Liq.' },
        { key: 'fechaLiquidacion', label: 'Fecha Liq.' },
        { key: 'contraparte', label: 'Contraparte' },
        { key: 'moneda', label: 'Moneda' },
        { key: 'comercial', label: 'Comercial' },
        { key: 'middleOffice', label: 'Middle Office' },
        { key: 'estado', label: 'Estado' },
        { key: 'comentarioComercial', label: 'Com. Comercial' },
        { key: 'comentarioMiddleOffice', label: 'Com. Middle Office' },
        { key: 'adjuntos', label: 'Adjuntos' },
    ];

    return (
        <div style={styles.container}>
            <div className="axon-wrap" style={{ ...styles.headerContainer, gap: '0.75rem' }}>
                <h2 className="axon-page-title" style={styles.mainTitle}>Instrucciones Operativas</h2>
                <div style={styles.headerActions}>
                    {canManage && (
                        <button
                            style={{ ...styles.button, ...styles.secondaryButton }}
                            onClick={handleExportExcel}
                            disabled={sortedInstrucciones.length === 0}
                            title="Descargar el listado en .xlsx, con los filtros y el orden aplicados"
                        >
                            Exportar a Excel ({sortedInstrucciones.length})
                        </button>
                    )}
                    {canCreate && (
                        <button className="axon-fab" style={styles.button} onClick={openNewModal}>+ Nueva Instrucción</button>
                    )}
                </div>
            </div>

            <div style={styles.filterContainer}>
                <div style={{ ...styles.filterHeader, gap: '0.75rem', ...(filtersPanel.isOpen ? {} : { borderBottom: 'none' }) }}>
                    <CollapsibleHeader isMobile={filtersPanel.isMobile} isOpen={filtersPanel.isOpen} onToggle={filtersPanel.toggle} style={styles.filterHeaderLeft}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                        <h3 style={styles.filterTitle}>Filtros</h3>
                        {hasActiveFilters && <span style={styles.filterBadge}>{Object.keys(filters).length}</span>}
                    </CollapsibleHeader>
                    {hasActiveFilters && (
                        <button style={styles.clearFiltersButton} onClick={clearFilters}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            Limpiar filtros
                        </button>
                    )}
                </div>
                {filtersPanel.isOpen && <div className="axon-grid-2" style={styles.filterGrid}>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Tipo</label>
                        <select style={{ ...styles.filterSelect, ...(filters.tipo ? styles.filterSelectActive : {}) }} value={filters.tipo || ''} onChange={e => handleFilterChange('tipo', e.target.value)}>
                            <option value="">Todos</option>
                            {formOptions.tipoInstruccion.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Nro Comitente</label>
                        <input style={{ ...styles.filterInput, ...(filters.nroComitente ? styles.filterInputActive : {}) }} type="text" placeholder="Ej: 568" value={filters.nroComitente || ''} onChange={e => handleFilterChange('nroComitente', e.target.value)} />
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Cliente</label>
                        <input style={{ ...styles.filterInput, ...(filters.cliente ? styles.filterInputActive : {}) }} type="text" placeholder="Nombre..." value={filters.cliente || ''} onChange={e => handleFilterChange('cliente', e.target.value)} />
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Moneda</label>
                        <select style={{ ...styles.filterSelect, ...(filters.moneda ? styles.filterSelectActive : {}) }} value={filters.moneda || ''} onChange={e => handleFilterChange('moneda', e.target.value)}>
                            <option value="">Todas</option>
                            {formOptions.monedaInstruccion.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Comercial</label>
                        <select style={{ ...styles.filterSelect, ...(filters.comercial ? styles.filterSelectActive : {}) }} value={filters.comercial || ''} onChange={e => handleFilterChange('comercial', e.target.value)}>
                            <option value="">Todos</option>
                            {uniqueComerciales.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Middle Office</label>
                        <select style={{ ...styles.filterSelect, ...(filters.middleOffice ? styles.filterSelectActive : {}) }} value={filters.middleOffice || ''} onChange={e => handleFilterChange('middleOffice', e.target.value)}>
                            <option value="">Todos</option>
                            {uniqueMiddleOffice.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Estado</label>
                        <select style={{ ...styles.filterSelect, ...(filters.estado ? styles.filterSelectActive : {}) }} value={filters.estado || ''} onChange={e => handleFilterChange('estado', e.target.value)}>
                            <option value="">Todos</option>
                            {['Programada', ...formOptions.estadoInstruccion].map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                </div>}
            </div>

            <TopScrollbar scrollRef={topRef} onScroll={onTopScroll} width={contentWidth} />

            <div style={styles.tableContainer} ref={contentRef} onScroll={onContentScroll}>
                {isLoading ? (
                    <p style={{ textAlign: 'center', padding: '2rem' }}>Cargando instrucciones...</p>
                ) : sortedInstrucciones.length === 0 ? (
                    <p style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textSecondary }}>
                        No hay instrucciones para mostrar.
                    </p>
                ) : (
                    <table ref={stackedTable.ref} className={stackedTable.className} style={styles.table}>
                        <thead>
                            <tr>
                                {tableHeaders.map(header => (
                                    <th key={header.key} style={{ ...styles.th, cursor: 'pointer' }} onClick={() => requestSort(header.key)}>
                                        {header.label}
                                        {sortConfig.key === header.key ? (sortConfig.direction === 'ascending' ? ' 🔼' : ' 🔽') : ''}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedInstrucciones.map(instr => {
                                const rowStyle = getRowStyle(instr.estado);
                                const isOwnInstruccion = instr.creadoPorId === userId;
                                const highlightOwn = isComercial && isOwnInstruccion;
                                const canEditRow = canEditInstruccion(instr);
                                const duplicados = duplicateGroups.get(instr.id) || [];
                                const duplicadosIds = duplicados.map(d => '#' + d.persistentId).join(', ');
                                const canDeleteRow = canCreate && isOwnInstruccion
                                    && (instr.estado === 'Pendiente' || instr.estado === 'Programada');
                                const canTomar = canManage && instr.estado === 'Pendiente';
                                const canChangeStatus = canManage
                                    && (instr.estado === 'En Proceso' || instr.estado === 'Pendiente');

                                return (
                                    <tr key={instr.id} style={rowStyle}>
                                        <td style={{ ...styles.td, ...(highlightOwn ? styles.ownRowMarker : {}) }}>
                                            {instr.persistentId}
                                            {duplicados.length > 0 && (
                                                <span
                                                    style={styles.duplicateBadge}
                                                    title={`Mismo cliente, tipo e instrumento que ${duplicadosIds}. Verificá que no sea la misma operación antes de procesarla.`}
                                                >
                                                    ⚠ {duplicadosIds}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            <button
                                                style={{ ...styles.iconButton, color: canEditRow ? theme.colors.primary : '#D1D5DB' }}
                                                onClick={() => handleEditClick(instr)}
                                                disabled={!canEditRow}
                                                title={canEditRow ? 'Editar' : 'No tiene permisos'}
                                            >
                                                <PencilIcon />
                                            </button>
                                            <button
                                                style={{ ...styles.iconButton, color: canDeleteRow ? theme.colors.errorText : '#D1D5DB' }}
                                                onClick={() => handleDelete(instr.id)}
                                                disabled={!canDeleteRow}
                                                title={canDeleteRow ? 'Eliminar' : 'No tiene permisos'}
                                            >
                                                <TrashIcon />
                                            </button>
                                            {canTomar && (
                                                <button
                                                    style={{ ...styles.pillButton, marginLeft: '0.4rem' }}
                                                    onClick={() => handleTomar(instr)}
                                                    title="Tomar instrucción"
                                                >
                                                    Tomar
                                                </button>
                                            )}
                                        </td>
                                        <td style={styles.td}>{safeFormatDate(instr.fechaOperativa)}</td>
                                        <td style={styles.td}>{safeFormatDate(instr.createdAt)}</td>
                                        <td style={styles.td}>
                                            {instr.tipo}
                                            {instr.viaEgreso === VIA_EGRESO_GC2 && (
                                                <span
                                                    style={styles.viaEgresoBadge}
                                                    title="Transferencia por MEP con código GC2 (rescate de FCI exento de débitos y créditos)"
                                                >
                                                    MEP GC2
                                                </span>
                                            )}
                                        </td>
                                        <td style={styles.td}>{formatComitenteForDisplay(instr.nroComitente)}</td>
                                        <td style={styles.td}>{instr.cliente}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>
                                            {instr.esRescateTotal
                                                ? <span style={{ fontStyle: 'italic', color: theme.colors.primary }}>Rescate Total</span>
                                                : (instr.monto ? formatCurrency(instr.monto, instr.moneda) : '-')}
                                        </td>
                                        {renderTitulosCells(instr)}
                                        <td style={styles.td}>{instr.fci || '-'}</td>
                                        <td style={styles.td}>{instr.plazoLiquidacion || '-'}</td>
                                        <td style={styles.td}>{instr.fechaLiquidacion || '-'}</td>
                                        <td style={styles.td}>{instr.contraparte || '-'}</td>
                                        <td style={styles.td}>{instr.moneda || '-'}</td>
                                        <td style={styles.td}>
                                            {highlightOwn ? <strong>{instr.comercial}</strong> : instr.comercial}
                                            {highlightOwn && <span style={styles.ownRowBadge}>Mía</span>}
                                        </td>
                                        <td style={styles.td}>{instr.middleOffice || '-'}</td>
                                        <td style={styles.td}>
                                            {canChangeStatus ? (
                                                <select
                                                    value={instr.estado}
                                                    onChange={(e) => handleStatusChange(instr, e.target.value)}
                                                    style={{ ...styles.select, marginTop: 0, padding: '0.25rem', backgroundColor: 'rgba(255,255,255,0.6)' }}
                                                >
                                                    {formOptions.estadoInstruccion.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                </select>
                                            ) : (
                                                instr.estado === 'Programada' && instr.fechaProgramada
                                                    ? `Programada · ${instr.fechaProgramada.split('-').reverse().slice(0, 2).join('/')}`
                                                    : instr.estado
                                            )}
                                        </td>
                                        <td style={styles.td}>{renderCommentCell(instr.comentarioComercial, 'Comentario Comercial', onViewComment)}</td>
                                        <td style={styles.td}>{renderCommentCell(instr.comentarioMiddleOffice, 'Comentario Middle Office', onViewComment)}</td>
                                        <td style={styles.td}>
                                            {Array.isArray(instr.adjuntos) && instr.adjuntos.length > 0 ? (
                                                <button
                                                    type="button"
                                                    style={styles.attachmentCellLink}
                                                    onClick={() => setAttachmentsModal({ isOpen: true, instr })}
                                                >
                                                    Ver ({instr.adjuntos.length})
                                                </button>
                                            ) : (
                                                <span style={styles.attachmentCellEmpty}>-</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal
                isOpen={isModalOpen}
                title={editingInstruccion ? 'Editar Instrucción' : 'Nueva Instrucción'}
                onSubmit={handleSubmit}
                error={formError}
                footer={<>
                    <button type="button" onClick={handleCancelModal} style={{ ...styles.button, ...styles.secondaryButton }}>Cancelar</button>
                    <button type="submit" style={styles.button} disabled={isUploading}>
                        {isUploading ? 'Subiendo adjuntos...' : 'Guardar'}
                    </button>
                </>}
            >
                <InstruccionForm
                    formState={formState}
                    saldoPreview={saldoPreview}
                    clientesDb={clientesDb}
                    especies={especies}
                    handleInputChange={handleInputChange}
                    handleTitulosChange={handleTitulosChange}
                    isReadOnly={false}
                    attachments={attachments}
                    onAttachFiles={handleAttachFiles}
                    onRemoveAttachment={handleRemoveAttachment}
                    isUploading={isUploading}
                    fondosFci={fondosFci}
                    onSeedFondosFci={esModoProductor ? undefined : handleSeedFondos}
                    isSeedingFondos={isSeedingFondos}
                    eligeComercial={eligeComercial}
                    nombresComerciales={nombresComerciales}
                />
            </Modal>

            <Modal
                isOpen={attachmentsModal.isOpen}
                title="Comprobantes adjuntos"
                maxWidth="600px"
                footer={
                    <button
                        onClick={() => setAttachmentsModal({ isOpen: false, instr: null })}
                        style={{ ...styles.button, ...styles.secondaryButton }}
                    >
                        Cerrar
                    </button>
                }
            >
                <p style={{ margin: '0 0 1rem 0', color: theme.colors.secondary, fontSize: '0.85rem' }}>
                    Instrucción #{attachmentsModal.instr?.persistentId} — {attachmentsModal.instr?.cliente}
                </p>
                <div style={styles.attachmentList}>
                    {(attachmentsModal.instr?.adjuntos || []).map((att, idx) => (
                        <div key={`${att.path}-${idx}`} style={styles.attachmentItem}>
                            <a
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={styles.attachmentLink}
                            >
                                {att.name}
                            </a>
                            <span style={styles.attachmentMeta}>{formatAttachmentSize(att.size)}</span>
                        </div>
                    ))}
                </div>
            </Modal>

            <Modal
                isOpen={rejectionModal.isOpen}
                title="Motivo de Rechazo"
                maxWidth="500px"
                footer={<>
                    <button onClick={() => setRejectionModal({ isOpen: false, instr: null })} style={{ ...styles.button, ...styles.secondaryButton }}>Cancelar</button>
                    <button onClick={handleRejectionSubmit} style={styles.button} disabled={!rejectionComment.trim()}>Confirmar Rechazo</button>
                </>}
            >
                <p style={{ margin: '0 0 1rem 0', color: theme.colors.secondary }}>
                    Indique el motivo por el cual se rechaza esta instrucción. El comercial podrá leerlo.
                </p>
                <textarea
                    value={rejectionComment}
                    onChange={(e) => setRejectionComment(e.target.value)}
                    style={styles.textarea}
                    autoFocus
                />
            </Modal>

            <DuplicateWarningModal
                isOpen={duplicateModal.isOpen}
                mode={duplicateModal.mode}
                entity="instruccion"
                candidate={duplicateModal.candidate}
                matches={duplicateModal.matches}
                onEditMatch={
                    duplicateModal.mode === 'alta' && canEditInstruccion(duplicateModal.matches[0])
                        ? handleEditFromDuplicate
                        : undefined
                }
                onContinue={() => {
                    const action = duplicateModal.pendingAction;
                    setDuplicateModal({ isOpen: false, mode: 'alta', matches: [], candidate: null, pendingAction: null });
                    action?.();
                }}
                onCancel={() => setDuplicateModal({ isOpen: false, mode: 'alta', matches: [], candidate: null, pendingAction: null })}
            />
        </div>
    );
};

export default InstruccionesTab;
